<script setup>
import { computed, provide, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useMainStore } from "@/stores/main.js";
import { useStyleStore } from "@/stores/style.js";
import MTabs from "@/components/m/MTabs.vue";
import MIcon from "@/components/m/MIcon.vue";
import MWorkspaceSheet from "@/components/m/MWorkspaceSheet.vue";
import MMoreSheet from "@/components/m/MMoreSheet.vue";
import MHome from "@/components/m/screens/MHome.vue";
import MReviews from "@/components/m/screens/MReviews.vue";
import MTopics from "@/components/m/screens/MTopics.vue";
import MCompetitors from "@/components/m/screens/MCompetitors.vue";
import MEntities from "@/components/m/screens/MEntities.vue";
import { Auth } from "@/services";

const mainStore = useMainStore();
const styleStore = useStyleStore();
const router = useRouter();
const route = useRoute();

/* The native shell owns these four primary screens. Their routes are kept in
   sync with the in-app tab so deep links, the browser back button and returns
   from secondary pages all land on the right tab. */
const TAB_ROUTES = {
  Home: "/",
  Reviews: "/reviews",
  Topics: "/topics",
  Entities: "/entities",
  Compare: "/competitors",
};
const ROUTE_TABS = {
  dashboard: "Home",
  reviews: "Reviews",
  topics: "Topics",
  entities: "Entities",
  competitors: "Compare",
};

/* ---- workspace mapping (real companies → switcher tiles) ---- */
const PALETTE = [
  "#EE9F12",
  "#1E9E6A",
  "#7C6BD8",
  "#3E73D6",
  "#DB5A55",
  "#0EA5A5",
];
const workspaces = computed(() =>
  (mainStore.companiesWatched || []).map((c, i) => {
    const color = PALETTE[i % PALETTE.length];
    return {
      id: c.company_id,
      name: c.company_name || "Workspace",
      initial: (c.company_name || "?").trim().charAt(0).toUpperCase() || "?",
      color,
      ink: color === "#EE9F12" ? "#2A1C00" : "#fff",
      sub: c.company_location || c.industry || "",
    };
  }),
);
const currentIdx = computed(() => {
  const id = mainStore.selectedCompany?.company_id;
  const idx = workspaces.value.findIndex((w) => String(w.id) === String(id));
  return idx >= 0 ? idx : 0;
});
const currentWorkspace = computed(
  () =>
    workspaces.value[currentIdx.value] || {
      id: null,
      name: "BuzzTracker",
      initial: "B",
      color: "#EE9F12",
      ink: "#2A1C00",
      sub: "",
    },
);
const accountUser = computed(() => ({
  name: mainStore.userName || mainStore.userEmail || "Account",
  sub: mainStore.userEmail || "",
}));

/* ---- tab + navigation intent ---- */
const tab = ref(ROUTE_TABS[route.name] || "Home");
const intent = ref(null);
const urgent = ref(null);
const wsOpen = ref(false);
const moreOpen = ref(false);

const go = (to, payload = null) => {
  intent.value = payload;
  tab.value = to;
  // keep the URL in sync with the active primary tab
  const path = TAB_ROUTES[to];
  if (path && route.path !== path) router.push(path);
};

// Navigate to a secondary page (rendered by the responsive LayoutAuthenticated).
const navigate = (to) => router.push(to);

// Reflect external route changes (back button / drawer links) back into the tab.
watch(
  () => route.name,
  (name) => {
    const mapped = ROUTE_TABS[name];
    if (mapped && mapped !== tab.value) {
      tab.value = mapped;
      intent.value = null;
    }
  },
);

const openWs = () => (wsOpen.value = true);
const openMore = () => (moreOpen.value = true);
const onPickWorkspace = (i) => {
  const company = mainStore.companiesWatched?.[i];
  if (company) mainStore.setSelectedCompany(company);
  wsOpen.value = false;
  if (tab.value !== "Home") go("Home");
};

/* ---- competitor read-only view ---- */
const competitors = computed(
  () => mainStore.selectedCompany?.competitors || [],
);
const isReadOnly = computed(() => mainStore.isReadOnly);
const viewingCompetitorId = computed(
  () => mainStore.viewingCompetitor?.company_id ?? null,
);
const viewingCompetitorName = computed(
  () => mainStore.viewingCompetitor?.name || "competitor",
);
const onViewCompetitor = (comp) => {
  mainStore.viewCompetitor(comp);
  wsOpen.value = false;
  if (tab.value !== "Home") go("Home");
};
const exitCompetitorView = () => mainStore.exitCompetitor();

const logout = async () => {
  wsOpen.value = false;
  try {
    await Auth.logout();
  } catch (error) {
    console.error("Logout request failed:", error);
  }
  mainStore.setOrganisation(null);
  mainStore.setUser(null);
  await router.push("/login");
};

const openProfile = async () => {
  wsOpen.value = false;
  await router.push("/profile");
};

const logoutFromMore = async () => {
  moreOpen.value = false;
  await logout();
};

/* ---- shared context for header / tabs / screens ---- */
const theme = computed(() => (styleStore.darkMode ? "dark" : "light"));
provide("mctx", {
  theme,
  tab,
  intent,
  urgent,
  workspace: currentWorkspace,
  toggle: () => styleStore.setDarkMode(),
  go,
  openWs,
  openMore,
  setUrgent: (n) => (urgent.value = n),
});

const SCREENS = {
  Home: MHome,
  Reviews: MReviews,
  Topics: MTopics,
  Entities: MEntities,
  Compare: MCompetitors,
};
const activeScreen = computed(() => SCREENS[tab.value] || MHome);
// remount the screen when navigation intent changes so per-screen filters apply
const screenKey = computed(
  () => tab.value + (intent.value ? JSON.stringify(intent.value) : ""),
);
</script>

<template>
  <div class="btm btm-root" :data-theme="theme">
    <div style="height: 100%; display: flex; flex-direction: column">
      <div v-if="isReadOnly" class="btm-ro-banner">
        <MIcon name="external" :size="14" />
        <span class="btm-ro-text"
          >Viewing <strong>{{ viewingCompetitorName }}</strong> —
          read-only</span
        >
        <button type="button" class="btm-ro-exit" @click="exitCompetitorView">
          Exit
        </button>
      </div>
      <div style="flex: 1; min-height: 0; position: relative">
        <!-- keep-alive preserves each tab's scroll position and avoids
             refetching on every switch; a changed navigation intent still
             forces a fresh mount via the screenKey. -->
        <keep-alive>
          <component :is="activeScreen" :key="screenKey" />
        </keep-alive>
      </div>
      <MTabs />
    </div>
    <MWorkspaceSheet
      :open="wsOpen"
      :list="workspaces"
      :current-idx="currentIdx"
      :user="accountUser"
      :competitors="competitors"
      :viewing-id="viewingCompetitorId"
      @close="wsOpen = false"
      @pick="onPickWorkspace"
      @view-competitor="onViewCompetitor"
      @logout="logout"
      @profile="openProfile"
    />
    <MMoreSheet
      :open="moreOpen"
      :is-superuser="mainStore.isSuperuser"
      :is-dark="styleStore.darkMode"
      @close="moreOpen = false"
      @navigate="navigate"
      @toggle-theme="styleStore.setDarkMode()"
      @logout="logoutFromMore"
    />
  </div>
</template>
